import { query } from "../../../../erd-editor-schema/dist/query/index.js";
import { arrayHas } from "../../../../utils/arrayHas.js";
import { isHighLevelTable } from "../../../../utils/validation.js";
import { FocusType, MoveKey } from "../state.js";
import { getColumnFocusTypes, getTableFocusTypes, toShownFocusType } from "./nameView.js";
import { appendSelectColumns } from "./selectRangeColumn.js";
//#region src/engine/modules/editor/utils/focus.ts
var TableFocusTypes = [
	FocusType.tableName,
	FocusType.tableLogicalName,
	FocusType.tableComment
];
function getColumnTypes({ settings }) {
	return getColumnFocusTypes(settings);
}
function indexOfFocus({ settings }, types, focusType) {
	return types.indexOf(toShownFocusType(settings, types, focusType) ?? focusType);
}
function isColumns({ collections, editor: { focusTable } }) {
	if (!focusTable) return false;
	const table = query(collections).collection("tableEntities").selectById(focusTable.tableId);
	return Boolean(table?.columnIds.length);
}
function isLastColumn(state) {
	const { editor: { focusTable } } = state;
	if (!focusTable) return true;
	const columnTypes = getColumnTypes(state);
	return indexOfFocus(state, columnTypes, focusTable.focusType) === columnTypes.length - 1;
}
function isFirstColumn(state) {
	const { editor: { focusTable } } = state;
	if (!focusTable) return true;
	return indexOfFocus(state, getColumnTypes(state), focusTable.focusType) === 0;
}
function isLastRowColumn({ collections, editor: { focusTable } }) {
	if (!focusTable?.columnId) return true;
	const table = query(collections).collection("tableEntities").selectById(focusTable.tableId);
	if (!table) return true;
	return table.columnIds.indexOf(focusTable.columnId) === table.columnIds.length - 1;
}
function isFirstRowColumn({ collections, editor: { focusTable } }) {
	if (!focusTable?.columnId) return true;
	const table = query(collections).collection("tableEntities").selectById(focusTable.tableId);
	if (!table) return true;
	return table.columnIds.indexOf(focusTable.columnId) === 0;
}
function getLastColumnType(state) {
	const columnTypes = getColumnTypes(state);
	return columnTypes[columnTypes.length - 1];
}
function getFirstColumnType(state) {
	return getColumnTypes(state)[0];
}
function getNextRightColumnType(state) {
	const { editor: { focusTable } } = state;
	if (!focusTable) return FocusType.columnName;
	const columnTypes = getColumnTypes(state);
	const index = indexOfFocus(state, columnTypes, focusTable.focusType);
	return isLastColumn(state) ? columnTypes[0] : columnTypes[index + 1];
}
function getNextLeftColumnType(state) {
	const { editor: { focusTable } } = state;
	if (!focusTable) return FocusType.columnName;
	const columnTypes = getColumnTypes(state);
	const index = indexOfFocus(state, columnTypes, focusTable.focusType);
	return isFirstColumn(state) ? columnTypes[columnTypes.length - 1] : columnTypes[index - 1];
}
function getRemoveFirstColumnId(state, columnIds) {
	const { collections, editor: { focusTable } } = state;
	if (!focusTable?.columnId) return null;
	const table = query(collections).collection("tableEntities").selectById(focusTable.tableId);
	if (!table) return null;
	const columnIndex = table.columnIds.indexOf(focusTable.columnId);
	if (columnIndex <= 0) return null;
	let columnId = null;
	for (let i = columnIndex; i >= 0; i--) {
		const currentColumnId = table.columnIds[i];
		if (!columnIds.includes(currentColumnId)) {
			columnId = currentColumnId;
			break;
		}
	}
	return columnId;
}
function getFocusedColumnIds({ editor: { focusTable }, collections }) {
	if (!focusTable?.columnId) return [];
	const table = query(collections).collection("tableEntities").selectById(focusTable.tableId);
	if (!table) return [];
	const { columnId } = focusTable;
	const selected = focusTable.selectColumnIds.filter(arrayHas(table.columnIds));
	return selected.length > 1 && selected.includes(columnId) ? selected : [columnId];
}
function getRemovableColumns({ doc: { tableIds, memoIds, tableGroupIds }, editor: { focusTable, selectedMap }, settings: { zoomLevel }, collections }) {
	if (!focusTable?.selectColumnIds.length || isHighLevelTable(zoomLevel)) return null;
	const selectedIds = Object.keys(selectedMap).filter(arrayHas([
		...tableIds,
		...memoIds,
		...tableGroupIds
	]));
	if (selectedIds.length !== 1 || selectedIds[0] !== focusTable.tableId) return null;
	const table = query(collections).collection("tableEntities").selectById(focusTable.tableId);
	if (!table) return null;
	const columnIds = focusTable.selectColumnIds.filter(arrayHas(table.columnIds));
	return columnIds.length ? {
		tableId: focusTable.tableId,
		columnIds
	} : null;
}
function getTableTypes({ settings }) {
	return getTableFocusTypes(settings);
}
function getFirstTableType(state) {
	return getTableTypes(state)[0];
}
function getLastTableType(state) {
	const tableTypes = getTableTypes(state);
	return tableTypes[tableTypes.length - 1];
}
function isLastTable(state) {
	const { editor: { focusTable } } = state;
	if (!focusTable) return true;
	const tableTypes = getTableTypes(state);
	return indexOfFocus(state, tableTypes, focusTable.focusType) === tableTypes.length - 1;
}
function isFirstTable(state) {
	const { editor: { focusTable } } = state;
	if (!focusTable) return true;
	return indexOfFocus(state, getTableTypes(state), focusTable.focusType) === 0;
}
var isTableFocusType = arrayHas(TableFocusTypes);
function getNextRightTableType(state) {
	const { editor: { focusTable } } = state;
	if (!focusTable) return FocusType.tableName;
	const tableTypes = getTableTypes(state);
	const index = indexOfFocus(state, tableTypes, focusTable.focusType);
	return isLastTable(state) ? tableTypes[0] : tableTypes[index + 1];
}
function getNextLeftTableType(state) {
	const { editor: { focusTable } } = state;
	if (!focusTable) return FocusType.tableName;
	const tableTypes = getTableTypes(state);
	const index = indexOfFocus(state, tableTypes, focusTable.focusType);
	return isFirstTable(state) ? tableTypes[tableTypes.length - 1] : tableTypes[index - 1];
}
function arrowUp(state, payload) {
	const { collections, editor: { focusTable } } = state;
	if (!focusTable) return;
	const table = query(collections).collection("tableEntities").selectById(focusTable.tableId);
	if (!table) return;
	if (isTableFocusType(focusTable.focusType)) {
		if (isColumns(state)) {
			const columnId = table.columnIds[table.columnIds.length - 1];
			focusTable.focusType = getLastColumnType(state);
			focusTable.columnId = columnId;
			focusTable.prevSelectColumnId = columnId;
			focusTable.selectColumnIds = [columnId];
		}
	} else if (isFirstRowColumn(state)) {
		focusTable.focusType = getFirstTableType(state);
		focusTable.columnId = null;
		focusTable.prevSelectColumnId = null;
		focusTable.selectColumnIds = [];
	} else if (focusTable.columnId) {
		const index = table.columnIds.indexOf(focusTable.columnId);
		const columnId = table.columnIds[index - 1];
		focusTable.columnId = columnId;
		focusTable.prevSelectColumnId = columnId;
		if (payload.shiftKey && payload.moveKey !== MoveKey.Tab) focusTable.selectColumnIds = appendSelectColumns(focusTable.selectColumnIds, columnId);
		else focusTable.selectColumnIds = [columnId];
	}
}
function arrowDown(state, payload) {
	const { collections, editor: { focusTable } } = state;
	if (!focusTable) return;
	const table = query(collections).collection("tableEntities").selectById(focusTable.tableId);
	if (!table) return;
	if (isTableFocusType(focusTable.focusType)) {
		if (isColumns(state)) {
			const columnId = table.columnIds[0];
			focusTable.focusType = getFirstColumnType(state);
			focusTable.columnId = columnId;
			focusTable.prevSelectColumnId = columnId;
			focusTable.selectColumnIds = [columnId];
		}
	} else if (isLastRowColumn(state)) {
		focusTable.focusType = getFirstTableType(state);
		focusTable.columnId = null;
		focusTable.prevSelectColumnId = null;
		focusTable.selectColumnIds = [];
	} else if (focusTable.columnId) {
		const index = table.columnIds.indexOf(focusTable.columnId);
		const columnId = table.columnIds[index + 1];
		focusTable.columnId = columnId;
		focusTable.prevSelectColumnId = columnId;
		if (payload.shiftKey && payload.moveKey !== MoveKey.Tab) focusTable.selectColumnIds = appendSelectColumns(focusTable.selectColumnIds, columnId);
		else focusTable.selectColumnIds = [columnId];
	}
}
function arrowLeft(state, payload) {
	const { collections, editor: { focusTable } } = state;
	if (!focusTable) return;
	const table = query(collections).collection("tableEntities").selectById(focusTable.tableId);
	if (!table) return;
	if (isTableFocusType(focusTable.focusType)) {
		if (isFirstTable(state)) {
			if (isColumns(state)) {
				const columnId = table.columnIds[table.columnIds.length - 1];
				focusTable.focusType = getLastColumnType(state);
				focusTable.columnId = columnId;
				focusTable.prevSelectColumnId = columnId;
				focusTable.selectColumnIds = [columnId];
			} else focusTable.focusType = getNextLeftTableType(state);
		} else focusTable.focusType = getNextLeftTableType(state);
	} else if (isFirstColumn(state)) {
		if (isFirstRowColumn(state)) {
			focusTable.focusType = getLastTableType(state);
			focusTable.columnId = null;
			focusTable.prevSelectColumnId = null;
			focusTable.selectColumnIds = [];
		} else if (focusTable.columnId) {
			const index = table.columnIds.indexOf(focusTable.columnId);
			const columnId = table.columnIds[index - 1];
			focusTable.focusType = getLastColumnType(state);
			focusTable.columnId = columnId;
			focusTable.prevSelectColumnId = columnId;
			if (payload.shiftKey && payload.moveKey !== MoveKey.Tab) focusTable.selectColumnIds = appendSelectColumns(focusTable.selectColumnIds, columnId);
			else focusTable.selectColumnIds = [columnId];
		}
	} else {
		focusTable.focusType = getNextLeftColumnType(state);
		if (!payload.shiftKey && focusTable.columnId) {
			focusTable.prevSelectColumnId = focusTable.columnId;
			focusTable.selectColumnIds = [focusTable.columnId];
		}
	}
}
function arrowRight(state, payload) {
	const { collections, editor: { focusTable } } = state;
	if (!focusTable) return;
	const table = query(collections).collection("tableEntities").selectById(focusTable.tableId);
	if (!table) return;
	if (isTableFocusType(focusTable.focusType)) {
		if (isLastTable(state)) {
			if (isColumns(state)) {
				const columnId = table.columnIds[0];
				focusTable.focusType = getFirstColumnType(state);
				focusTable.columnId = columnId;
				focusTable.prevSelectColumnId = columnId;
				focusTable.selectColumnIds = [columnId];
			} else focusTable.focusType = getNextRightTableType(state);
		} else focusTable.focusType = getNextRightTableType(state);
	} else if (isLastColumn(state)) {
		if (isLastRowColumn(state)) {
			focusTable.focusType = getFirstTableType(state);
			focusTable.columnId = null;
			focusTable.prevSelectColumnId = null;
			focusTable.selectColumnIds = [];
		} else if (focusTable.columnId) {
			const index = table.columnIds.indexOf(focusTable.columnId);
			const columnId = table.columnIds[index + 1];
			focusTable.focusType = getFirstColumnType(state);
			focusTable.columnId = columnId;
			focusTable.prevSelectColumnId = columnId;
			if (payload.shiftKey && payload.moveKey !== MoveKey.Tab) focusTable.selectColumnIds = appendSelectColumns(focusTable.selectColumnIds, columnId);
			else focusTable.selectColumnIds = [columnId];
		}
	} else {
		focusTable.focusType = getNextRightColumnType(state);
		if (!payload.shiftKey && focusTable.columnId) {
			focusTable.prevSelectColumnId = focusTable.columnId;
			focusTable.selectColumnIds = [focusTable.columnId];
		}
	}
}
//#endregion
export { arrowDown, arrowLeft, arrowRight, arrowUp, getFirstColumnType, getFocusedColumnIds, getRemovableColumns, getRemoveFirstColumnId, isColumns, isLastColumn, isLastRowColumn, isLastTable, isTableFocusType };
